<template>
  <div class="section-wrapper">
    <section class="about-section" id="about" aria-label="About section">
      <div class="background-elements">
        <div class="grid-overlay"></div>
        <div class="floating-elements" aria-hidden="true">
          <div class="tech-circle"></div>
          <div class="tech-square"></div>
          <div class="tech-dots"></div>
        </div>
      </div>

      <div class="content-container">
        <div class="about-content">
          <div class="brand-tag slide-in" data-delay="100">
            <div class="tag-dot"></div>
            <span>Why Choose Us</span>
          </div>

          <h2 class="main-heading">
            <div class="heading-line fade-in" data-delay="300">
              Reasons to Choose
            </div>
            <div class="heading-highlight fade-in" data-delay="500">
              Vision Lab
              <svg
                class="heading-accent"
                viewBox="0 0 100 100"
                width="80"
                height="80"
              >
                <circle cx="50" cy="50" r="40" class="accent-circle" />
              </svg>
            </div>
          </h2>

          <div class="about-cards">
            <div
              v-for="(card, index) in cards"
              :key="card.title"
              class="about-card fade-in"
              :data-delay="300 + index * 100"
            >
              <div class="card-icon">
                <i :class="card.icon"></i>
              </div>
              <h3 class="card-title">{{ card.title }}</h3>
              <p class="card-description">{{ card.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { onMounted, onBeforeUnmount } from "vue";

export default {
  name: "AboutSection",
  setup() {
    const cards = [
      {
        title: "Custom Website Design",
        description:
          "Tailored website design to fit your brand and enhance user experience.",
        icon: "bi-layout-wtf",
      },
      {
        title: "Telegram Bot Creation",
        description:
          "Develop Telegram bots for automating tasks, managing customer interactions, and increasing efficiency.",
        icon: "bi-chat-left-dots",
      },
      {
        title: "E-commerce Solutions",
        description:
          "We create fully integrated e-commerce websites to help you sell products and grow your business online.",
        icon: "bi-shop-window",
      },
      {
        title: "API Integrations",
        description:
          "Seamless API integrations to connect your app, website, or bot with third-party services.",
        icon: "bi-code-slash",
      },
    ];
    let observer = null;

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const delay = entry.target.dataset.delay || 0;
          setTimeout(() => {
            entry.target.classList.add("visible");
          }, delay);
        }
      });
    };

    onMounted(() => {
      const elements = document.querySelectorAll(".fade-in, .slide-in");
      observer = new IntersectionObserver(handleIntersection, {
        threshold: 0.1,
        rootMargin: "20px",
      });

      elements.forEach((el) => observer.observe(el));

      // Mouse movement parallax effect
      const handleMouseMove = (e) => {
        const floatingElements = document.querySelectorAll(
          ".floating-elements > div"
        );
        const { clientX, clientY } = e;
        const centerX = window.innerWidth / 2;
        const centerY = window.innerHeight / 2;

        floatingElements.forEach((element, index) => {
          const speed = 0.01 + index * 0.01;
          const x = (clientX - centerX) * speed;
          const y = (clientY - centerY) * speed;
          element.style.transform = `translate(${x}px, ${y}px)`;
        });
      };

      document.addEventListener("mousemove", handleMouseMove);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
      };
    });

    onBeforeUnmount(() => {
      if (observer) {
        observer.disconnect();
      }
    });

    return {
      cards,
    };
  },
};
</script>

<style lang="scss" scoped>
.section-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #000000;
  position: relative;
  overflow: hidden;
  padding: 2rem 0; /* Reduced padding for natural content height */
}

.about-section {
  --primary-color: #6366f1;
  --accent-color: #818cf8;
  --text-primary: #ffffff;
  --text-secondary: #e2e8f0;

  width: 100%;
  padding: 4rem 0; /* Adjusted padding */
}

.background-elements {
  position: fixed; /* Changed to fixed to cover the entire viewport */
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}

.gradient-sphere {
  position: absolute;
  top: -20%;
  right: -10%;
  width: 80vh;
  height: 80vh;
  background: radial-gradient(
    circle,
    rgba(99, 102, 241, 0.2) 0%,
    rgba(99, 102, 241, 0.1) 40%,
    transparent 70%
  );
  border-radius: 50%;
}

.grid-overlay {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
      rgba(61, 115, 255, 0.03) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(61, 115, 255, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: radial-gradient(circle at 50% 50%, black, transparent 80%);
}

.content-container {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
}

.brand-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 100px;
  margin-bottom: 2rem;

  .tag-dot {
    width: 8px;
    height: 8px;
    background: var(--accent-color);
    border-radius: 50%;
    animation: pulse 2s infinite;
  }

  span {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-primary);
    letter-spacing: 0.05em;
  }
}

.main-heading {
  margin-bottom: 4rem;

  .heading-line {
    font-size: clamp(1.5rem, 3vw, 2rem);
    color: var(--text-secondary);
    margin-bottom: 0.5rem;
  }

  .heading-highlight {
    position: relative;
    font-size: clamp(2.5rem, 5vw, 4rem);
    font-weight: 700;
    color: var(--text-primary);
    line-height: 1.1;
  }
}

.about-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.about-card {
  padding: 2rem;
  background: rgba(99, 102, 241, 0.05);
  border: 1px solid rgba(99, 102, 241, 0.1);
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    background: rgba(99, 102, 241, 0.1);
  }

  .card-icon {
    width: 48px;
    height: 48px;
    margin-bottom: 1.5rem;
    padding: 0.75rem;
    background: rgba(99, 102, 241, 0.1);
    border-radius: 12px;

    display: flex;
    justify-content: center; /* Centers the icon horizontally */
    align-items: center; /* Centers the icon vertically */

    i {
      font-size: 24px; /* Adjust icon size */
      color: var(--accent-color);
    }
  }

  .card-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 1rem;
  }

  .card-description {
    color: var(--text-secondary);
    line-height: 1.6;
  }
}

.fade-in {
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s cubic-bezier(0.22, 1, 0.36, 1);

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
}

.slide-in {
  opacity: 0;
  transform: translateX(-20px);
  transition: all 0.6s cubic-bezier(0.22, 1, 0.36, 1);

  &.visible {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.5;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .about-section {
    padding: 2rem 0; /* Further reduced padding for mobile */
  }

  .about-cards {
    grid-template-columns: 1fr;
  }

  .floating-elements {
    display: none;
  }

  .section-wrapper {
    padding: 1rem 0; /* Reduced padding for mobile */
  }
}
</style>
